import { RevealText, FadeIn } from "../components/RevealText";
import { SUPPORT } from "../lib/api";

const SECTIONS = [
  {
    n: "01",
    title: "Information We Collect",
    body: [
      "When you use saveu, we collect information you provide directly — such as your name, email address, and campus — when you create an account or contact support.",
      "We also collect usage data such as venues you view, deals you redeem, check-ins, and points activity to power the busyness meters, rewards, and leaderboard features.",
      "With your permission, we may collect approximate location data to show you nearby bars, restaurants, and live busyness near your campus.",
    ],
  },
  {
    n: "02",
    title: "How We Use Your Information",
    body: [
      "To provide core features: live venue busyness, student deals, the points system, and the campus leaderboard.",
      "To personalize recommendations, improve the app, and keep saveu safe and reliable.",
      "To communicate with you about support requests, account activity, and important updates.",
    ],
  },
  {
    n: "03",
    title: "Sharing Your Information",
    body: [
      "We do not sell your personal information. We may share limited data with venues and partners to deliver deals and verify redemptions.",
      "We may share information with service providers who help us operate the app, and where required by law.",
      "Leaderboard rankings may display a username or handle to other users; you control what is publicly visible.",
    ],
  },
  {
    n: "04",
    title: "Your Choices & Rights",
    body: [
      "You can access, update, or delete your account information at any time from within the app or by contacting us.",
      "You can disable location permissions through your device settings, though some features may be limited.",
      `To request deletion of your data, email us at ${SUPPORT.email} and we will process your request.`,
    ],
  },
  {
    n: "05",
    title: "Data Security & Retention",
    body: [
      "We use industry-standard measures to protect your information. No method of transmission or storage is completely secure, but we work hard to safeguard your data.",
      "We retain your information for as long as your account is active or as needed to provide the service and comply with legal obligations.",
    ],
  },
  {
    n: "06",
    title: "Children's Privacy",
    body: [
      "saveu is intended for college students and users aged 17 and older. We do not knowingly collect information from anyone under this age.",
    ],
  },
  {
    n: "07",
    title: "Changes & Contact",
    body: [
      "We may update this Privacy Policy from time to time. Material changes will be communicated in the app or by email.",
      `Questions about this policy? Contact us at ${SUPPORT.email} or ${SUPPORT.phoneDisplay}.`,
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen">
      <section className="px-6 lg:px-10 pt-40 pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="font-body uppercase tracking-[0.4em] text-xs text-brand-dark mb-8">
            Legal
          </p>
          <RevealText
            as="h1"
            lines={["Privacy", "Policy."]}
            className="font-display font-black uppercase tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            delay={0.15}
          />
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-xl text-lg text-ink-soft leading-relaxed">
              Your privacy matters to us. This policy explains what saveu
              collects, how we use it, and the choices you have.
            </p>
            <p className="mt-4 text-sm text-ink-faint">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-32">
        <div className="mx-auto max-w-4xl space-y-16">
          {SECTIONS.map((s) => (
            <FadeIn key={s.n}>
              <article
                data-testid={`privacy-section-${s.n}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6"
              >
                <div className="md:col-span-3">
                  <span className="font-display font-black text-5xl text-brand/25 leading-none">
                    {s.n}
                  </span>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-display font-bold text-2xl tracking-tight text-ink mb-5">
                    {s.title}
                  </h2>
                  <div className="space-y-4">
                    {s.body.map((p, i) => (
                      <p key={i} className="text-ink-soft leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </section>
    </div>
  );
}
